import {
  CheckOutlined,
  DeleteOutlined,
  PlusOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import { Button, Empty, Input, Popover, Tree } from 'antd';
import cn from 'classnames';
import { useMemo, useState } from 'react';

import { getStageColors } from '@app/components/LeadCard/ui/LeadFunnelStages/utils/getStageColors';

import type {
  LeadFunnelStageOption,
  LeadFunnelStagesControlMode,
  LeadFunnelStageViewModel,
} from './types';
import type { TreeDataNode, TreeProps } from 'antd';
import type { FC, Key, ReactNode } from 'react';

import './LeadFunnelStagesControl.scss';

interface FunnelStageGroup {
  funnelId: number;
  funnelName: string;
  options: LeadFunnelStageOption[];
}

interface FunnelStagePickerProps {
  mode: LeadFunnelStagesControlMode;
  options: LeadFunnelStageOption[];
  selectedStageId?: number | null;
  disabled?: boolean;
  emptyText: string;
  children: ReactNode;
  onSelect(stageId: number): void;
}

interface LeadFunnelStagesControlProps {
  stages: LeadFunnelStageViewModel[];
  options: LeadFunnelStageOption[];
  isLoading?: boolean;
  isDisabled?: boolean;
  onAddStage(stageId: number): void;
  onChangeStage(currentStageId: number, nextStageId: number): void;
  onDeleteStage(stageId: number): void;
}

const EMPTY_STAGE_TEXT = 'Этапы не назначены';
const UNNAMED_FUNNEL = 'Без названия';
const UNNAMED_STAGE = 'Без названия';
const FUNNEL_TREE_KEY_PREFIX = 'funnel-';

// Group options by funnel so the popover can render a funnel header followed by
// a flat list of its stages. The change-stage picker receives a single funnel
// (one header + stage list); the add-stage picker may receive several funnels
// (each rendered as its own labelled section) without any expandable tree.
const groupOptionsByFunnel = (
  options: LeadFunnelStageOption[]
): FunnelStageGroup[] => {
  const groups = new Map<number, FunnelStageGroup>();

  options.forEach((option) => {
    const group = groups.get(option.funnelId);

    if (group) {
      group.options.push(option);

      return;
    }

    groups.set(option.funnelId, {
      funnelId: option.funnelId,
      funnelName: option.funnelName || UNNAMED_FUNNEL,
      options: [option],
    });
  });

  return [...groups.values()];
};

const normalizeSearchValue = (value: string) => value.trim().toLowerCase();

const filterGroupsBySearchValue = (
  groups: FunnelStageGroup[],
  searchValue: string
): FunnelStageGroup[] => {
  const normalizedSearchValue = normalizeSearchValue(searchValue);

  if (!normalizedSearchValue) return groups;

  return groups.reduce<FunnelStageGroup[]>((acc, group) => {
    const isFunnelMatched = group.funnelName
      .toLowerCase()
      .includes(normalizedSearchValue);

    if (isFunnelMatched) {
      acc.push(group);

      return acc;
    }

    const options = group.options.filter((option) =>
      (option.stageName || UNNAMED_STAGE)
        .toLowerCase()
        .includes(normalizedSearchValue)
    );

    if (options.length > 0) {
      acc.push({
        ...group,
        options,
      });
    }

    return acc;
  }, []);
};

// Add-mode tree: funnels as non-selectable roots, stages as selectable leaves.
// The stage leaf key is the numeric stage id so the select handler can map it
// straight to the add mutation, exactly like the previous tree implementation.
const buildTreeData = (groups: FunnelStageGroup[]): TreeDataNode[] =>
  groups.map((group) => ({
    key: `${FUNNEL_TREE_KEY_PREFIX}${group.funnelId}`,
    title: (
      <span className="LeadFunnelStagesControl__tree-funnel-name">
        {group.funnelName}
      </span>
    ),
    className:
      'LeadFunnelStagesControl__tree-node LeadFunnelStagesControl__tree-node--funnel',
    selectable: false,
    children: group.options.map((option) => ({
      key: option.stageId,
      className:
        'LeadFunnelStagesControl__tree-node LeadFunnelStagesControl__tree-node--stage',
      title: (
        <span className="LeadFunnelStagesControl__picker-option">
          <span
            className="LeadFunnelStagesControl__picker-color"
            style={{ backgroundColor: option.color ?? undefined }}
          />
          <span className="LeadFunnelStagesControl__picker-name">
            {option.stageName || UNNAMED_STAGE}
          </span>
        </span>
      ),
    })),
  }));

const FunnelStagePicker: FC<FunnelStagePickerProps> = ({
  mode,
  options,
  selectedStageId = null,
  disabled = false,
  emptyText,
  children,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Key[]>([]);
  const [treeSearchValue, setTreeSearchValue] = useState('');
  const groups = useMemo(() => groupOptionsByFunnel(options), [options]);
  const filteredGroups = useMemo(
    () =>
      mode === 'add-stage-assignment'
        ? filterGroupsBySearchValue(groups, treeSearchValue)
        : groups,
    [mode, groups, treeSearchValue]
  );
  const treeData = useMemo(
    () =>
      mode === 'add-stage-assignment' ? buildTreeData(filteredGroups) : [],
    [mode, filteredGroups]
  );
  const treeExpandedKeys = useMemo(
    () =>
      normalizeSearchValue(treeSearchValue)
        ? filteredGroups.map(
            (group) => `${FUNNEL_TREE_KEY_PREFIX}${group.funnelId}`
          )
        : expandedKeys,
    [expandedKeys, filteredGroups, treeSearchValue]
  );

  const selectHandler = (stageId: number) => {
    setIsOpen(false);
    onSelect(stageId);
  };

  const treeSelectHandler: TreeProps['onSelect'] = (_keys, { node }) => {
    if (typeof node.key !== 'number') return;

    selectHandler(node.key);
  };

  const treeExpandHandler: TreeProps['onExpand'] = (keys) => {
    setExpandedKeys(keys);
  };

  const openChangeHandler = (nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);

    if (nextIsOpen && mode === 'add-stage-assignment') {
      setExpandedKeys([]);
      setTreeSearchValue('');
    }
  };

  if (disabled) return <>{children}</>;

  let content: ReactNode;

  if (groups.length === 0) {
    content = (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={emptyText}
        className="LeadFunnelStagesControl__empty"
      />
    );
  } else if (mode === 'add-stage-assignment') {
    content = (
      <div className="LeadFunnelStagesControl__tree-panel">
        <Input
          className="LeadFunnelStagesControl__tree-search"
          allowClear
          size="small"
          placeholder="Поиск"
          value={treeSearchValue}
          onChange={(event) => setTreeSearchValue(event.target.value)}
        />

        {filteredGroups.length > 0 ? (
          <Tree
            className="LeadFunnelStagesControl__tree"
            treeData={treeData}
            blockNode
            expandedKeys={treeExpandedKeys}
            selectedKeys={selectedStageId !== null ? [selectedStageId] : []}
            onExpand={treeExpandHandler}
            onSelect={treeSelectHandler}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Ничего не найдено"
            className="LeadFunnelStagesControl__empty"
          />
        )}
      </div>
    );
  } else {
    content = (
      <div className="LeadFunnelStagesControl__picker">
        {groups.map((group) => (
          <div
            className="LeadFunnelStagesControl__picker-group"
            key={group.funnelId}
          >
            <div className="LeadFunnelStagesControl__picker-header">
              <span className="LeadFunnelStagesControl__picker-header-label">
                Воронка
              </span>
              <span
                className="LeadFunnelStagesControl__picker-header-name"
                title={group.funnelName}
              >
                {group.funnelName}
              </span>
            </div>

            <ul className="LeadFunnelStagesControl__picker-list">
              {group.options.map((option) => {
                const isSelected = option.stageId === selectedStageId;

                return (
                  <li key={option.stageId}>
                    <button
                      className={cn('LeadFunnelStagesControl__picker-item', {
                        'LeadFunnelStagesControl__picker-item--selected':
                          isSelected,
                      })}
                      type="button"
                      onClick={() => selectHandler(option.stageId)}
                    >
                      <span
                        className="LeadFunnelStagesControl__picker-color"
                        style={{ backgroundColor: option.color ?? undefined }}
                      />
                      <span className="LeadFunnelStagesControl__picker-name">
                        {option.stageName || UNNAMED_STAGE}
                      </span>
                      {isSelected && (
                        <CheckOutlined className="LeadFunnelStagesControl__picker-check" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Popover
      content={content}
      open={isOpen}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      classNames={{ root: 'LeadFunnelStagesControl__overlay' }}
      onOpenChange={openChangeHandler}
    >
      {children}
    </Popover>
  );
};

export const LeadFunnelStagesControl: FC<LeadFunnelStagesControlProps> = ({
  stages,
  options,
  isLoading = false,
  isDisabled = false,
  onAddStage,
  onChangeStage,
  onDeleteStage,
}) => {
  const usedFunnelIds = useMemo(
    () => new Set(stages.map((stage) => stage.funnelId)),
    [stages]
  );
  const availableOptionsForAdd = useMemo(
    () => options.filter((option) => !usedFunnelIds.has(option.funnelId)),
    [options, usedFunnelIds]
  );
  const optionsByFunnelId = useMemo(() => {
    const map = new Map<number, LeadFunnelStageOption[]>();

    options.forEach((option) => {
      const funnelOptions = map.get(option.funnelId);

      if (funnelOptions) {
        funnelOptions.push(option);

        return;
      }

      map.set(option.funnelId, [option]);
    });

    return map;
  }, [options]);
  const disabled = isLoading || isDisabled;
  const [firstStage, ...remainingStages] = stages;

  const renderStageChip = (stage: LeadFunnelStageViewModel) => {
    const colors = getStageColors(stage.color);
    const stageOptions = optionsByFunnelId.get(stage.funnelId) ?? [];

    return (
      <div
        className="LeadFunnelStagesControl__chip"
        key={`${stage.funnelId}-${stage.stageId}`}
        style={{
          background: colors.background,
          color: colors.text,
        }}
      >
        <FunnelStagePicker
          mode="edit-current-stage"
          options={stageOptions}
          selectedStageId={stage.stageId}
          disabled={disabled}
          emptyText="Этапы недоступны"
          onSelect={(nextStageId) => onChangeStage(stage.stageId, nextStageId)}
        >
          <button
            className="LeadFunnelStagesControl__trigger"
            type="button"
            disabled={disabled}
            title={`${stage.funnelName} - ${stage.stageName}`}
          >
            <span className="LeadFunnelStagesControl__funnel">
              {stage.funnelName}
            </span>
            <span className="LeadFunnelStagesControl__stage">
              {stage.stageName}
            </span>
          </button>
        </FunnelStagePicker>

        <Button
          className="LeadFunnelStagesControl__remove"
          type="text"
          size="small"
          icon={<DeleteOutlined />}
          aria-label="Удалить этап"
          disabled={disabled}
          style={{ color: colors.text }}
          onClick={() => onDeleteStage(stage.stageId)}
        />
      </div>
    );
  };

  const addStageButton =
    availableOptionsForAdd.length > 0 ? (
      <FunnelStagePicker
        mode="add-stage-assignment"
        options={availableOptionsForAdd}
        disabled={disabled}
        emptyText="Нет доступных воронок"
        onSelect={onAddStage}
      >
        <Button
          className="LeadFunnelStagesControl__add"
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          disabled={disabled}
        />
      </FunnelStagePicker>
    ) : null;

  return (
    <div className="LeadFunnelStagesControl">
      <div className="LeadFunnelStagesControl__row">
        {firstStage ? (
          renderStageChip(firstStage)
        ) : (
          <span className="LeadFunnelStagesControl__empty-text">
            {EMPTY_STAGE_TEXT}
          </span>
        )}

        {addStageButton}
      </div>

      {remainingStages.length > 0 && (
        <Popover
          content={
            <div className="LeadFunnelStagesControl__more-list">
              {stages.map(renderStageChip)}
            </div>
          }
          trigger="click"
          placement="bottomLeft"
          arrow={false}
          classNames={{ root: 'LeadFunnelStagesControl__more-overlay' }}
        >
          <Button
            className="LeadFunnelStagesControl__more"
            type="link"
            size="small"
            icon={<UnorderedListOutlined />}
            disabled={disabled}
          >
            Показать все
          </Button>
        </Popover>
      )}
    </div>
  );
};
